/**
 * Course settings payload helper.
 *
 * The backend now persists per-course settings (display prefs, instructor
 * messaging, CE info, event verification, author, course code) on a
 * `CourseSettings` blob that rides on `AddCourseRequest.settings`. The
 * server returns the same values flattened onto the top-level `Course`
 * fields it hands back.
 *
 * Several tabs of the course editor each save independently via
 * `updateCourse`, merging their slice onto the loaded course. To stop one
 * tab's save from wiping settings another tab owns, every save rebuilds the
 * full `settings` object from the currently-loaded course and overrides only
 * the field(s) that tab edits. `courseToSettings` is that round-trip: it
 * reads the flattened fields off a loaded `Course` back into the nested
 * `CourseSettings` shape the save expects.
 */

import { Course, CourseSettings } from '../apis/api-client';

/**
 * Project a loaded course's flattened settings fields back into the nested
 * `CourseSettings` shape. Spread the result into an `updateCourse` payload
 * and override the field(s) the current tab edits:
 *
 *   settings: { ...courseToSettings(course), ceInfo, ceInfoEnabled }
 */
export function courseToSettings(course: Course | null | undefined): CourseSettings {
  return {
    displayModuleNames: course?.displayModuleNames,
    instructorMessagingEnabled: course?.instructorMessagingEnabled,
    instructorMessagingEmail: course?.instructorMessagingEmail,
    ceInfoEnabled: course?.ceInfoEnabled,
    issuesCertificate: course?.issuesCertificate,
    courseCode: course?.courseCode,
    authorId: course?.authorId,
    ceInfo: course?.ceInfo,
    eventVerification: course?.eventVerification,
    detailVideoUrl: course?.detailVideoUrl,
    detailVideoUrlEnabled: course?.detailVideoUrlEnabled,
    ceHours: course?.ceHours,
  };
}
